<template>
    <CustomDialog v-model="visible" :defaultTitle="$t('experience.planningModal.title')" width="700px" @cancel="$emit('cancel')">
        <template #content>
            <div class="ppv2-space-y-4">
                <!-- Date range -->
                <div class="ppv2-flex" :style="{ gap: normalizeSize(16, true) }">
                    <div class="ppv2-flex-1">
                        <BaseDateInput
                            v-model="dateRange.startDate"
                            :label="$t('experience.planningModal.dateStart')"
                            :placeholder="$t('experience.planningModal.dateStart')"
                            displayFormat="dddd D MMMM YYYY"
                            inputClass="!ppv2-rounded-lg !ppv2-h-[30px] !ppv2-min-h-[30px]"
                            :customSizeLabel="12"
                        />
                    </div>
                    <div class="ppv2-flex-1">
                        <BaseDateInput
                            v-model="dateRange.endDate"
                            :label="$t('experience.planningModal.dateEnd')"
                            :placeholder="$t('experience.planningModal.dateEnd')"
                            :min-date="dateRange.startDate"
                            displayFormat="dddd D MMMM YYYY"
                            inputClass="!ppv2-rounded-lg !ppv2-h-[30px] !ppv2-min-h-[30px]"
                            :customSizeLabel="12"
                        />
                    </div>
                </div>

                <!-- Jours spécifiques -->
                <div class="ppv2-flex ppv2-flex-col">
                    <h3 class="poppinsMedium ppv2-text-black ppv2-text-xs ppv2-mb-2">
                        {{ $t('experience.planningModal.dayRepetition') }}
                    </h3>
                    <div class="ppv2-flex ppv2-flex-wrap ppv2-gap-2">
                        <BaseCircleButton
                            v-for="day in daysList"
                            :key="day.id"
                            :active="selectedDays.includes(day.id)"
                            @click="toggleDay(day.id)"
                        >
                            {{ day.label }}
                        </BaseCircleButton>
                    </div>
                    <p v-if="hint" class="creneau-hint poppinsRegular">{{ hint }}</p>
                </div>

                <!-- Créneaux list (variant-card style) -->
                <div v-for="(item, index) in items" :key="index" class="creneau-card">
                    <!-- Mode « créneaux personnalisés » (billetterie) : le créneau vient de l'étape
                         Disponibilités, ses heures sont verrouillées ; seul le prix se saisit. -->
                    <div v-if="hasPresets && item.label" class="creneau-preset-label poppinsMedium">
                        {{ item.label }}<span v-if="item.nextDay"> {{ $t('experience.planningModal.nextDaySuffix') }}</span>
                    </div>
                    <!-- Row: Start Date, End Date, Prix, TVA, Actions -->
                    <div class="creneau-row creneau-padding">
                        <!-- Heure début -->
                        <div v-if="!wholeDay" class="creneau-field creneau-field--time">
                            <BaseTimeInput
                                v-model="item.startTime"
                                :label="$t('experience.planningModal.startTime')"
                                :required="true"
                                :disabled="hasPresets"
                                :customSizeLabel="10"
                                inputClass="!ppv2-rounded-lg !ppv2-h-[30px] !ppv2-min-h-[30px]"
                                :step="1"
                                placeholder="--:--"
                            />
                        </div>

                        <!-- Heure fin -->
                        <div v-if="!wholeDay" class="creneau-field creneau-field--time">
                            <BaseTimeInput
                                v-model="item.endTime"
                                :label="$t('experience.planningModal.endTime')"
                                :required="true"
                                :disabled="hasPresets"
                                :customSizeLabel="10"
                                inputClass="!ppv2-rounded-lg !ppv2-h-[30px] !ppv2-min-h-[30px]"
                                :step="1"
                                :minTime="item.startTime"
                                placeholder="--:--"
                            />
                        </div>

                        <!-- Prix -->
                        <div class="creneau-field creneau-field--prix">
                            <BaseInput
                                v-model="item.price"
                                :label="$t('experience.planningModal.sellingPrice')"
                                :suffix="$symbolCurrency"
                                :required="!hasPresets"
                                :placeholder="hasPresets ? $t('experience.planningModal.basePricePlaceholder') : ''"
                                type="number"
                                :min="0"
                                step="0.01"
                                classInputAdd="!ppv2-rounded-lg !ppv2-h-[30px] !ppv2-min-h-[30px]"
                                classContainerAdd="!ppv2-opacity-100 !ppv2-mb-0"
                                :customSizeLabel="11"
                                customClassLabel="poppinsMedium"
                            />
                        </div>

                        <!-- Action buttons (Copy + Delete) -->
                        <div class="creneau-actions">
                            <button
                                v-if="!wholeDay && !hasPresets"
                                type="button"
                                class="creneau-action-btn"
                                :disabled="items.length >= effectiveMaxItems"
                                @click="duplicateItem(index)"
                            >
                                <Copy :size="14" />
                            </button>
                            <button
                                v-if="items.length > 1 && !hasPresets"
                                type="button"
                                class="creneau-action-btn"
                                @click="deleteItem(index)"
                            >
                                <X :size="14" />
                            </button>
                        </div>
                    </div>
                </div>

                <!-- + Ajouter un créneau button (dashed style) -->
                <button
                    v-if="!wholeDay && !hasPresets"
                    type="button"
                    class="creneau-add-btn"
                    :disabled="items.length >= effectiveMaxItems"
                    @click="addItem"
                >
                    <Plus :size="16" />
                    <span class="poppinsMedium">{{ $t('experience.planningModal.addCreneau') }}</span>
                </button>

                <!-- Conflict message -->
                <div v-if="conflictMessage" class="ppv2-text-red-500 ppv2-text-sm ppv2-mt-2">
                    {{ conflictMessage }}
                </div>
                <div v-if="errorMsg" class="ppv2-text-red-500 ppv2-text-sm ppv2-mt-2">
                    {{ errorMsg }}
                </div>
            </div>
        </template>

        <template #actions>
            <BaseButton type="button" variant="outline" customClass="!ppv2-min-h-[40px]" :customHeight="43" @click="close">
                {{ $t('common.cancel') }}
            </BaseButton>
            <BaseButton
                type="button"
                variant="primary"
                customClass="!ppv2-min-h-[40px]"
                :customHeight="43"
                :loading="isLoading"
                @click="handleSubmit"
            >
                {{ $t('common.save') }}
            </BaseButton>
        </template>
    </CustomDialog>
</template>

<script>
import normalizeSize from '../../../../utils/normalizeSize'

import CustomDialog from '../../../Shared/CustomDialog.vue'
import BaseButton from '../../../Shared/BaseButton.vue'
import BaseLabel from '../../../Shared/BaseLabel.vue'
import BaseInput from '../../../Shared/BaseInput.vue'
import BaseSelect from '../../../Shared/BaseSelect.vue'
import CustomInput from '../../../Inputs/CustomInput.vue'
import BaseDateInput from '../../../Shared/BaseDateInput.vue'
import BaseTimeInput from '../../../Shared/BaseTimeInput.vue'
import BaseCircleButton from '../../../Shared/BaseCircleButton.vue'
import { Plus, Copy, X } from 'lucide-vue'
import axios from 'axios'

export default {
    name: 'PlanningModal',

    components: {
        CustomDialog,
        BaseButton,
        BaseLabel,
        BaseInput,
        BaseSelect,
        CustomInput,
        BaseDateInput,
        BaseTimeInput,
        BaseCircleButton,
        Plus,
        Copy,
        X,
    },

    props: {
        value: { type: Boolean, default: false },
        prestationId: { type: [Number, String], default: null },
        establishmentId: { type: [Number, String], default: 1 },
        selectedSlot: { type: Object, default: null },
        mode: { type: String, default: 'create' },
        prestation: { type: Object, default: null },
        apiMode: { type: String, default: 'direct' }, // 'direct' or 'emit'
        // Scenarios already configured (used to block duplicates before saving).
        existingTarifs: { type: Array, default: () => [] },
        // simpleTicket "Options de billetterie": a specific price applies to the WHOLE day, not a
        // time slot — hide the start/end time inputs and force each créneau to 00:00–23:59.
        // (mode « Toute la journée » de l'étape Disponibilités uniquement ; un billet historique sans
        // mode saisit des plages horaires libres, comme `fixed`)
        wholeDay: { type: Boolean, default: false },
        // simpleTicket, mode « Créneaux personnalisés » : les créneaux de l'étape Disponibilités
        // sont pré-listés ({ label, startTime, endTime, nextDay }), heures verrouillées, un prix
        // chacun. Prix vide = prix de base (la ligne n'est pas envoyée).
        presetSlots: { type: Array, default: () => [] },
        // Texte d'aide sous la répétition par jours (ex. règle des créneaux fixes).
        hint: { type: String, default: '' },
        // Créneaux pré-listés : une soumission SANS aucun prix ne retire que les règles de la même
        // clé (plage + jours). Si une autre plage couvre les dates éditées, le parent le signale ici
        // et le modal refuse : il faut saisir le prix de base explicitement pour l'écraser.
        resetBlocked: { type: Boolean, default: false },
    },

    data() {
        return {
            isLoading: false,
            items: [],
            dateRange: { startDate: null, endDate: null },
            selectedDays: [],
            conflictMessage: '',
            errorMsg: '',
            maxItems: 5,
            daysList: [
                { id: 'Monday', label: this.$t('experience.timeSpecific.days.mon') },
                { id: 'Tuesday', label: this.$t('experience.timeSpecific.days.tue') },
                { id: 'Wednesday', label: this.$t('experience.timeSpecific.days.wed') },
                { id: 'Thursday', label: this.$t('experience.timeSpecific.days.thu') },
                { id: 'Friday', label: this.$t('experience.timeSpecific.days.fri') },
                { id: 'Saturday', label: this.$t('experience.timeSpecific.days.sat') },
                { id: 'Sunday', label: this.$t('experience.timeSpecific.days.sun') },
            ],
        }
    },

    computed: {
        visible: {
            get() {
                return this.value
            },
            set(val) {
                this.$emit('input', val)
            },
        },
        formattedDateStart() {
            return this.dateRange.startDate || ''
        },
        formattedDateEnd() {
            return this.dateRange.endDate || ''
        },
        hasPresets() {
            return !this.wholeDay && Array.isArray(this.presetSlots) && this.presetSlots.length > 0
        },
        effectiveMaxItems() {
            return Math.max(this.maxItems, this.presetSlots.length)
        },
    },

    watch: {
        value(val) {
            if (val) {
                this.initForm()
            }
        },
    },

    methods: {
        normalizeSize,
        initForm() {
            this.items = []
            this.conflictMessage = ''
            this.errorMsg = ''
            this.selectedDays = []

            if (this.selectedSlot) {
                // Set date range from selected slot
                const start = this.selectedSlot.startStr || this.selectedSlot.start || this.selectedSlot.dateStr
                const end = this.selectedSlot.endStr || this.selectedSlot.end || start
                this.dateRange.startDate = typeof start === 'string' ? start.substring(0, 10) : this.formatDate(start)
                this.dateRange.endDate = typeof end === 'string' ? end.substring(0, 10) : this.formatDate(end)

                // Restore selectedDays from the slot if available
                if (
                    this.selectedSlot.selectedDays &&
                    Array.isArray(this.selectedSlot.selectedDays) &&
                    this.selectedSlot.selectedDays.length > 0
                ) {
                    this.selectedDays = [...this.selectedSlot.selectedDays]
                } else {
                    // No saved selectedDays — default to the day-of-week of the clicked date
                    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
                    const startDate = this.dateRange.startDate
                    if (startDate) {
                        const d = new Date(startDate + 'T00:00:00')
                        this.selectedDays = [dayNames[d.getDay()]]
                    }
                }

                // If editing existing event with planningPrices
                const prices = this.selectedSlot.extendedProps?.planningPrices || this.selectedSlot.planningPrices
                if (this.hasPresets) {
                    this.seedFromPresets(prices || [])
                } else if (prices && prices.length > 0) {
                    this.pushItems(prices)
                } else {
                    this.addItem()
                }
            } else if (this.hasPresets) {
                this.seedFromPresets([])
            } else {
                this.addItem()
            }
        },

        // Une ligne par créneau de l'étape Disponibilités. Le prix repris est celui dont la plage
        // contient le DÉBUT du créneau — la règle de facturation (getPrestationPrice) — sinon vide,
        // c'est-à-dire prix de base.
        seedFromPresets(planningPrices) {
            const hi = (t) => (t || '').substring(0, 5)
            this.items = this.presetSlots.map((preset) => {
                const startTime = hi(preset.startTime)
                const endTime = hi(preset.endTime)
                const match = (planningPrices || []).find((pp) => hi(pp.start_time) <= startTime && startTime < hi(pp.end_time))
                return {
                    label: preset.label || '',
                    nextDay: !!preset.nextDay,
                    startTime,
                    endTime,
                    price: match ? parseFloat(match.price || 0).toFixed(2) : '',
                    off_peak_time: match ? !!match.off_peak_time : true,
                    authorize_mixing: match && match.authorize_mixing !== undefined ? !!match.authorize_mixing : true,
                }
            })
        },

        formatDate(date) {
            if (!date) return ''
            const d = new Date(date)
            const y = d.getFullYear()
            const m = String(d.getMonth() + 1).padStart(2, '0')
            const day = String(d.getDate()).padStart(2, '0')
            return `${y}-${m}-${day}`
        },

        pushItems(planningPrices) {
            const seen = new Set()
            for (const pp of planningPrices) {
                const [hS, mS] = (pp.start_time || '').split(':').slice(0, 2)
                const [hE, mE] = (pp.end_time || '').split(':').slice(0, 2)
                // Whole-day mode: a date carries a single all-day price, so collapse to one row.
                const key = this.wholeDay ? 'wholeday' : `${hS}:${mS}|${hE}:${mE}`
                if (seen.has(key)) continue
                seen.add(key)
                this.items.push({
                    startTime: this.wholeDay ? '00:00' : `${hS}:${mS}`,
                    endTime: this.wholeDay ? '23:59' : `${hE}:${mE}`,
                    price: parseFloat(pp.price || 0).toFixed(2),
                    off_peak_time: !!pp.off_peak_time,
                    authorize_mixing: pp.authorize_mixing !== undefined ? !!pp.authorize_mixing : true,
                })
            }
        },

        addItem() {
            const defaultPrice = this.prestation?.price || ''
            this.items.push({
                startTime: '00:00',
                endTime: this.wholeDay ? '23:59' : '00:00',
                price: defaultPrice ? parseFloat(defaultPrice).toFixed(2) : '',
                off_peak_time: true,
                authorize_mixing: true,
            })
        },

        toggleDay(dayId) {
            const idx = this.selectedDays.indexOf(dayId)
            if (idx === -1) {
                this.selectedDays.push(dayId)
            } else {
                this.selectedDays.splice(idx, 1)
            }
        },

        deleteItem(index) {
            this.items.splice(index, 1)
        },

        duplicateItem(index) {
            if (this.items.length >= this.maxItems) return
            const source = this.items[index]
            this.items.splice(index + 1, 0, { ...source })
        },

        checkForConflicts() {
            for (let i = 0; i < this.items.length; i++) {
                const a = this.items[i]
                const aStart = new Date(`1970/01/01 ${a.startTime}`)
                const aEnd = new Date(`1970/01/01 ${a.endTime}`)
                for (let j = i + 1; j < this.items.length; j++) {
                    const b = this.items[j]
                    const bStart = new Date(`1970/01/01 ${b.startTime}`)
                    const bEnd = new Date(`1970/01/01 ${b.endTime}`)
                    if (aStart < bEnd && bStart < aEnd) {
                        return true
                    }
                }
            }
            return false
        },

        validateItems() {
            for (const item of this.items) {
                if (!item.startTime || !item.endTime || !item.price) {
                    this.errorMsg = this.$t('experience.planningModal.errorRequired')
                    return false
                }
                if (item.startTime >= item.endTime) {
                    this.errorMsg = this.$t('experience.planningModal.errorTimeRange')
                    return false
                }
            }
            return true
        },

        handleSubmit() {
            this.conflictMessage = ''
            this.errorMsg = ''

            // Créneaux pré-listés : heures verrouillées (donc ni ordre ni chevauchement à vérifier —
            // l'étape Disponibilités l'interdit déjà) ; un prix vide = prix de base, la ligne n'est
            // pas envoyée. Aucune ligne remplie ⇒ le parent retire les règles de ces dates qui ont la
            // même clé (plage + jours) ; si une autre plage les couvre encore, on refuse (resetBlocked).
            const items = this.hasPresets
                ? this.items.filter((item) => item.price !== '' && item.price !== null && item.price !== undefined)
                : this.items

            if (this.hasPresets && items.length === 0 && this.resetBlocked) {
                this.errorMsg = this.$t('experience.planningModal.errorResetCoveredDay')
                return
            }

            if (!this.hasPresets && !this.validateItems()) return

            // Overlaps within the créneaux being saved are rejected here; the backend save is
            // authoritative per date (it purges + recreates the edited dates), so re-saving a
            // date that already has tarifs simply replaces them — no cross-existing check needed
            // (that produced false "tarif already exists" alerts when re-editing a day).
            if (!this.wholeDay && !this.hasPresets && this.checkForConflicts()) {
                this.conflictMessage = this.$t('experience.planningModal.conflictError')
                return
            }

            // A day-of-week subset cannot be persisted without a bounded end date
            // (an open-ended/sentinel row has no day mask). Mirror of the backend guard.
            const isDaySubset = this.selectedDays.length > 0 && this.selectedDays.length < this.daysList.length
            if (isDaySubset && !this.dateRange.endDate) {
                this.errorMsg = this.$t('experience.planningModal.errorEndDateRequired')
                return
            }

            let finalSelectedDays = [...this.selectedDays]

            // If no days selected, default to all days of the week
            if (finalSelectedDays.length === 0) {
                finalSelectedDays = this.daysList.map((day) => day.id)
            }

            const payload = {
                days: finalSelectedDays,
                range: this.dateRange,
                // label / nextDay sont des attributs d'affichage des créneaux pré-listés.
                items: items.map(({ label, nextDay, ...item }) => item),
                planningId: this.selectedSlot?.planningId || null,
            }

            this.submit(payload)
        },

        async submit(payload) {
            this.isLoading = true
            this.errorMsg = ''

            if (this.apiMode === 'emit') {
                // Return payload without calling the API
                this.isLoading = false
                this.visible = false
                this.$emit('submit-rule', payload)
                return
            }

            try {
                await axios.post(`/api/v3/experience/planning/${this.prestationId}/${this.establishmentId}`, payload)
                this.isLoading = false
                this.visible = false
                this.$emit('refresh', payload)
            } catch (error) {
                this.$toast.error(error.response.data.message)
                this.isLoading = false
                this.errorMsg = error.response?.data?.error || error.response?.data?.message || 'Erreur lors de la sauvegarde.'
            }
        },

        close() {
            this.visible = false
            this.$emit('cancel')
        },
    },
}
</script>

<style scoped>
/* Créneau card (matches variant-card style from StepTicketOptions) */
.creneau-card {
    position: relative;
    border-radius: 12px;
    border: 1px solid #545595;
    background-color: #1a1f370d;
    padding: 0;
    margin-bottom: 12px;
    transition: all 0.2s ease;
}
.dark .creneau-card {
    background-color: rgba(255, 255, 255, 0.05);
}

.creneau-padding {
    padding: 16px 20px;
}

/* Créneau main row — horizontal flex */
.creneau-row {
    display: flex;
    align-items: flex-end;
    gap: 16px;
}

.creneau-field {
    flex-shrink: 0;
}
.creneau-field--time {
    flex: 1 1 0;
    min-width: 100px;
}
.creneau-field--prix {
    width: 110px;
}

/* Action icon buttons */
.creneau-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 2px;
}
.creneau-action-btn {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: 1px solid rgba(84, 85, 149, 0.4);
    background-color: rgba(255, 255, 255, 0.05);
    cursor: pointer;
    color: rgba(0, 0, 0, 0.9);
    transition: all 0.15s ease;
}
.creneau-action-btn:hover {
    background-color: rgba(84, 85, 149, 0.1);
}
.creneau-action-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}
.dark .creneau-action-btn {
    color: rgba(255, 255, 255, 0.9);
    border-color: rgba(255, 255, 255, 0.15);
}

/* Créneau pré-listé (mode « créneaux personnalisés ») */
.creneau-preset-label {
    padding: 10px 20px 0;
    font-size: 12px;
    color: #545595;
}
.dark .creneau-preset-label {
    color: rgba(255, 255, 255, 0.8);
}
.creneau-hint {
    margin-top: 8px;
    font-size: 11px;
    color: #6b7280;
}

/* Add créneau button (dashed style) */
.creneau-add-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    padding: 10px;
    border: 1px dashed #545595;
    border-radius: 12px;
    background-color: #1a1f370d;
    color: #545595;
    cursor: pointer;
    font-size: 13px;
    transition: all 0.2s ease;
    margin-top: 4px;
}
.creneau-add-btn:hover {
    background-color: rgba(84, 85, 149, 0.08);
}
.creneau-add-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}
</style>
